import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

let turnstileScriptPromise: Promise<void> | null = null;

export function preloadTurnstileScript() {
  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Turnstile")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = "true";
    script.onload = () => resolve();
    script.onerror = () => {
      turnstileScriptPromise = null;
      reject(new Error("Failed to load Turnstile"));
    };
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface TurnstileWidgetProps {
  siteKey: string;
  onTokenChange: (token: string | null) => void;
  onError?: (message: string) => void;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { siteKey, onTokenChange, onError },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
        onTokenChangeRef.current(null);
      },
    }),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function setupWidget() {
      try {
        setReady(false);
        await preloadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback(token) {
            onTokenChangeRef.current(token);
            setReady(true);
          },
          "expired-callback": () => {
            onTokenChangeRef.current(null);
            onErrorRef.current?.("Turnstile expired. Please verify again.");
          },
          "timeout-callback": () => {
            onTokenChangeRef.current(null);
            onErrorRef.current?.("Turnstile timed out. Please verify again.");
          },
          "error-callback": errorCode => {
            onTokenChangeRef.current(null);
            onErrorRef.current?.(`Turnstile failed to verify (${String(errorCode)}). Please try again.`);
            return false;
          },
          appearance: "always",
          theme: "auto",
        });

        setReady(true);
      } catch (error) {
        onTokenChangeRef.current(null);
        onErrorRef.current?.(error instanceof Error ? error.message : "Failed to load Turnstile.");
      }
    }

    void setupWidget();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="min-h-[65px]" />
      {!ready ? <p className="text-xs text-muted-foreground">Loading Turnstile…</p> : null}
    </div>
  );
});
